// ProductCard.jsx — Premium gift card with MUI icons, wishlist, hover effects, and quick-add

import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "react-toastify";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { formatCurrency } from "../utils/pricing";
import { truncate } from "../utils/helpers";

// MUI Icons
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import FavoriteIcon from "@mui/icons-material/Favorite";
import AddShoppingCartIcon from "@mui/icons-material/AddShoppingCart";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import StarHalfIcon from "@mui/icons-material/StarHalf";
import AddIcon from "@mui/icons-material/Add";

// Category colour mapping
const CATEGORY_STYLES = {
  "Gift Items": { bg: "bg-rose-100", text: "text-rose-700", dot: "bg-rose-400" },
  "Toys": { bg: "bg-amber-100", text: "text-amber-700", dot: "bg-amber-400" },
  "Games": { bg: "bg-blue-100", text: "text-blue-700", dot: "bg-blue-400" },
  "Handicrafts": { bg: "bg-emerald-100", text: "text-emerald-700", dot: "bg-emerald-400" },
};

// Star rating using MUI icons
function StarRating({ rating = 0 }) {
  const stars = [];
  const full = Math.floor(rating);
  const half = rating % 1 >= 0.5;

  for (let i = 0; i < 5; i++) {
    if (i < full) stars.push(<StarIcon key={i} sx={{ fontSize: 13, color: "#fbbf24" }} />);
    else if (i === full && half) stars.push(<StarHalfIcon key={i} sx={{ fontSize: 13, color: "#fbbf24" }} />);
    else stars.push(<StarBorderIcon key={i} sx={{ fontSize: 13, color: "#d1d5db" }} />);
  }
  return <div className="flex items-center gap-0.5">{stars}</div>;
}

// Wishlist heart button (localStorage persisted)
function WishlistButton({ productId }) {
  const [wishlisted, setWishlisted] = useState(false);
  const [animating, setAnimating] = useState(false);
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const list = JSON.parse(localStorage.getItem("giftcraft_wishlist") || "[]");
    setWishlisted(list.includes(productId));
  }, [productId]);

  function toggle(e) {
    e.preventDefault();
    e.stopPropagation();

    if (!currentUser) {
      toast.info("Please login to manage your wishlist.");
      navigate("/login");
      return;
    }

    const list = JSON.parse(localStorage.getItem("giftcraft_wishlist") || "[]");
    const next = wishlisted ? list.filter((id) => id !== productId) : [...list, productId];
    localStorage.setItem("giftcraft_wishlist", JSON.stringify(next));
    setWishlisted(!wishlisted);
    setAnimating(true);
    setTimeout(() => setAnimating(false), 600);
    window.dispatchEvent(new Event("wishlist_updated"));
  }

  return (
    <motion.button
      whileTap={{ scale: 0.8 }}
      onClick={toggle}
      aria-label={wishlisted ? "Remove from wishlist" : "Add to wishlist"}
      className={`absolute top-3 right-3 w-8 h-8 rounded-full flex items-center justify-center transition-all shadow-md z-10
        ${wishlisted ? "bg-rose-500 text-white shadow-gift-sm" : "bg-white/90 text-gray-400 hover:text-rose-500 hover:bg-white"}
        ${animating ? "heart-beat" : ""}`}
    >
      {wishlisted
        ? <FavoriteIcon sx={{ fontSize: 16 }} />
        : <FavoriteBorderIcon sx={{ fontSize: 16 }} />
      }
    </motion.button>
  );
}

export default function ProductCard({ product, index = 0 }) {
  const { addToCart, cartItems } = useCart();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [addedAnim, setAddedAnim] = useState(false);
  const [imgLoaded, setImgLoaded] = useState(false);

  const inCart = cartItems.some((i) => i.id === product.id);
  const catStyle = CATEGORY_STYLES[product.category] || { bg: "bg-gray-100", text: "text-gray-600", dot: "bg-gray-400" };

  // Show a fake discount badge on some products based on id hash
  const discountPct = ((product.id?.charCodeAt(0) || 65) % 3 === 0) ? 20
    : ((product.id?.charCodeAt(0) || 65) % 3 === 1) ? 15
      : 0;
  const originalPrice = discountPct > 0 ? (product.price / (1 - discountPct / 100)).toFixed(2) : null;

  function handleAddToCart(e) {
    e.preventDefault();
    e.stopPropagation();

    if (!currentUser) {
      toast.info("Please login to add items to your cart.");
      navigate("/login");
      return;
    }

    if (product.stock === 0) return;
    addToCart(product);
    setAddedAnim(true);
    setTimeout(() => setAddedAnim(false), 1800);
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.45, delay: index * 0.06, ease: "easeOut" }}
    >
      <Link
        to={`/product/${product.id}`}
        className="group block bg-white rounded-3xl overflow-hidden flex flex-col card-lift border border-gray-100/80 hover:border-rose-100"
        style={{ boxShadow: "0 2px 16px rgba(0,0,0,0.06)" }}
      >
        {/* ── Image ─────────────────────────────────────────────── */}
        <div className="relative overflow-hidden bg-gradient-to-br from-rose-50 to-pink-50" style={{ height: "220px" }}>
          {!imgLoaded && <div className="absolute inset-0 skeleton" />}

          <motion.img
            src={product.image}
            alt={product.name}
            loading="lazy"
            onLoad={() => setImgLoaded(true)}
            className={`w-full h-full object-cover transition-all duration-500 group-hover:scale-110 ${imgLoaded ? "opacity-100" : "opacity-0"}`}
            onError={(e) => { e.target.src = "https://placehold.co/400x300/fce7f3/e879f9?text=Gift"; setImgLoaded(true); }}
          />

          {/* Hover overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

          {/* Wishlist */}
          <WishlistButton productId={product.id} />

          {/* Badges */}
          <div className="absolute top-3 left-3 flex flex-col gap-1.5">
            {product.stock === 0 && (
              <span className="bg-gray-700/90 text-white text-[10px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wide backdrop-blur-sm">
                Sold Out
              </span>
            )}
            {product.stock > 0 && product.stock <= 5 && (
              <span className="bg-amber-500 text-white text-[10px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wide shadow-gold-sm">
                Only {product.stock} left!
              </span>
            )}
            {discountPct > 0 && product.stock > 0 && (
              <span className="bg-gradient-to-r from-rose-500 to-pink-600 text-white text-[10px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wide shadow-gift-sm">
                {discountPct}% OFF
              </span>
            )}
          </div>

          {/* Quick-add on hover */}
          <div className="absolute bottom-0 left-0 right-0 p-3 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={handleAddToCart}
              disabled={product.stock === 0}
              className={`w-full py-2 rounded-xl text-xs font-bold uppercase tracking-wide transition-all shadow-lg flex items-center justify-center gap-1.5
                ${product.stock === 0
                  ? "bg-gray-400 text-white cursor-not-allowed"
                  : inCart
                    ? "bg-emerald-500 text-white"
                    : "bg-gradient-to-r from-rose-500 to-pink-600 text-white hover:opacity-90"
                }`}
            >
              {product.stock === 0
                ? "Out of Stock"
                : inCart
                  ? <><CheckCircleOutlineIcon sx={{ fontSize: 15 }} /> In Cart</>
                  : <><AddIcon sx={{ fontSize: 15 }} /> Quick Add</>
              }
            </motion.button>
          </div>
        </div>

        {/* ── Card body ─────────────────────────────────────────── */}
        <div className="p-4 flex flex-col flex-1">
          {/* Category badge */}
          <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-0.5 rounded-full self-start mb-2 ${catStyle.bg} ${catStyle.text}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${catStyle.dot}`} />
            {product.category}
          </span>

          {/* Product name */}
          <h3 className="font-semibold text-gray-900 text-sm leading-snug mb-1.5 group-hover:text-rose-600 transition-colors line-clamp-2">
            {product.name}
          </h3>

          {/* Description */}
          <p className="text-xs text-gray-500 mb-3 flex-1 line-clamp-2 leading-relaxed">
            {truncate(product.description, 72)}
          </p>

          {/* Rating */}
          <div className="flex items-center gap-1.5 mb-3">
            <StarRating rating={product.rating || 0} />
            <span className="text-xs text-gray-500">
              {(product.rating || 0).toFixed(1)}
              <span className="text-gray-300 mx-1">·</span>
              {product.reviews || 0} reviews
            </span>
          </div>

          {/* Price + Add to Cart */}
          <div className="flex items-center justify-between mt-auto pt-3 border-t border-gray-50">
            <div>
              <span className="text-lg font-bold text-gray-900">{formatCurrency(product.price)}</span>
              {originalPrice && (
                <span className="text-xs text-gray-400 line-through ml-1.5">{formatCurrency(parseFloat(originalPrice))}</span>
              )}
            </div>

            <motion.button
              whileTap={{ scale: 0.92 }}
              onClick={handleAddToCart}
              disabled={product.stock === 0}
              className={`flex items-center gap-1.5 text-xs px-4 py-2 rounded-xl font-semibold transition-all
                ${product.stock === 0
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : inCart
                    ? "bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100"
                    : "bg-gradient-to-r from-rose-500 to-pink-600 text-white shadow-gift-sm hover:shadow-gift hover:opacity-95"
                }`}
            >
              <AnimatePresence mode="wait">
                {addedAnim ? (
                  <motion.span key="added" initial={{ scale: 0 }} animate={{ scale: 1 }} exit={{ scale: 0 }} className="flex items-center gap-1">
                    <CheckCircleOutlineIcon sx={{ fontSize: 14 }} /> Added!
                  </motion.span>
                ) : inCart ? (
                  <motion.span key="incart" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex items-center gap-1">
                    <ShoppingCartIcon sx={{ fontSize: 14 }} /> In Cart
                  </motion.span>
                ) : (
                  <motion.span key="add" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex items-center gap-1">
                    <AddShoppingCartIcon sx={{ fontSize: 14 }} /> Add to Cart
                  </motion.span>
                )}
              </AnimatePresence>
            </motion.button>
          </div>
        </div>
      </Link>
    </motion.div>
  );
}
